import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { appLinks } from '../../lib/crossSiteLinks';
import { tokens } from '../../components/SEOPageLayout';
import { BreadcrumbJsonLd, FaqJsonLd } from '../../components/StructuredData';

export default function AuditTimelineFeaturePage() {
  const faqs = [
    {
      question: 'What is an audit timeline?',
      answer: 'An audit timeline is a chronological record of software change events, including approvals, checks, merges, and deployments, generated directly from GitHub metadata.'
    },
    {
      question: 'Does the audit timeline include source code?',
      answer: 'No. EvidentTrail captures metadata only and does not store source code or free-text review comments.'
    },
    {
      question: 'How far back does the timeline go?',
      answer: 'Retention is plan-based (7, 30, or 90 days). Evidence packs can be exported within the retention window.'
    },
    {
      question: 'Can auditors use the timeline for sampling?',
      answer: 'Yes. Auditors can filter evidence by date range, repository, or event type to locate sampled changes quickly.'
    }
  ];

  return (
    <MarketingLayout
      title="Audit Timeline - Chronological Evidence of Software Changes"
      description="The EvidentTrail audit timeline provides a chronological record of all software changes, approvals, and deployments. System-generated evidence for auditors."
      h1="Audit Timeline"
      heroText="A chronological, system-generated record of all software changes in your repositories. See who changed what, when, and whether it was properly approved."
      type="article"
    >
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', url: 'https://evidenttrail.com/' },
          { name: 'Audit Timeline', url: 'https://evidenttrail.com/features/audit-timeline' }
        ]}
      />
      <FaqJsonLd items={faqs} />
      {/* What It Produces */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>What the Audit Timeline Produces</h2>
            <div className={tokens.card}>
              <p className={`${tokens.text.base} mb-6`}>
                The timeline captures each significant event in your software development lifecycle:
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>
                    <strong className="text-white">Pull request opened</strong> — Author, title, description, target branch
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>
                    <strong className="text-white">Review submitted</strong> — Reviewer identity, approval/request changes, timestamp
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>
                    <strong className="text-white">CI checks completed</strong> — Check name, pass/fail status, duration
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>
                    <strong className="text-white">Pull request merged</strong> — Merge timestamp, merge commit SHA, merger identity
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>
                    <strong className="text-white">Release events</strong> — Tag name, release status, timestamp <span className="text-slate-500">(Enterprise)</span>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                  <span className={tokens.text.base}>
                    <strong className="text-white">AI-assisted context</strong> — Flags when changes involved AI tools, with linked human approval records
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Auditors Care */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Why Auditors Care</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-3`}>Complete Audit Trail</h3>
                <p className={tokens.text.base}>
                  Every change is recorded with timestamps and identities, providing a complete chain of
                  custody for software modifications.
                </p>
              </div>
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-3`}>System-Generated</h3>
                <p className={tokens.text.base}>
                  Evidence comes directly from GitHub's API, not from manual documentation.
                  This provides higher assurance for auditors.
                </p>
              </div>
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-3`}>Easy Sampling</h3>
                <p className={tokens.text.base}>
                  Auditors can filter by date range, repository, or event type to quickly find
                  the changes they need to verify.
                </p>
              </div>
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-3`}>Continuous Coverage</h3>
                <p className={tokens.text.base}>
                  Unlike point-in-time samples, the timeline covers the entire audit period
                  without gaps.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sample Evidence */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Sample Timeline Event</h2>
            <div className={`${tokens.card} font-mono text-sm`}>
              <pre className="text-slate-300 overflow-x-auto">
                {`{
  "eventType": "pull_request.merged",
  "timestamp": "2026-01-02T14:32:18Z",
  "repository": "acme-corp/api-service",
  "pullRequest": {
    "number": 847,
    "title": "Add rate limiting to auth endpoints",
    "author": "jane.developer",
    "approvals": [
      {
        "reviewer": "senior.engineer",
        "approvedAt": "2026-01-02T13:45:22Z"
      }
    ],
    "checksPassedAt": "2026-01-02T14:28:11Z",
    "mergedBy": "jane.developer",
    "mergeCommit": "a1b2c3d4..."
  }
}`}
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Frequently Asked Questions</h2>
            <div className="space-y-6">
              {faqs.map((item) => (
                <div key={item.question} className={tokens.card}>
                  <h3 className={`${tokens.heading.h3} mb-2`}>{item.question}</h3>
                  <p className={tokens.text.base}>{item.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-2xl mx-auto text-center">
            <h2 className={`${tokens.heading.h2} mb-4`}>Start Building Your Audit Timeline</h2>
            <p className={`${tokens.text.base} mb-8`}>
              Connect your GitHub repositories and start capturing evidence automatically.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href={appLinks.register} className="btn btn-primary text-lg px-8 py-3">
                Get Started
              </a>
              <Link to="/features/drift-detection" className="text-slate-300 hover:text-white transition-colors">
                Learn about Drift Detection →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
